import type { Timestamp } from "@firebase/firestore-types";
import { z } from "zod";
import type { TrackedCategorySplit } from "../trackedCategory/models.js";

/** Bridge sandbox Demo Bank (fake data only — skip AI categorization). */
export const BRIDGE_DEMO_PROVIDER_ID = 574;

const BridgeTransactionSchema2021 = z.object({
  id: z.number(),
  date: z.string(),
  amount: z.number(),
  category_id: z.number(),
  clean_description: z.string(),
  currency_code: z.string(),
  is_deleted: z.boolean(),
  is_future: z.boolean(),
  show_client_side: z.boolean().optional().default(true),
  updated_at: z.string(),
});

// Migrate 2025 changes back to 2021 version
function migrateTransaction2025To2021(input: unknown) {
  if (typeof input === "object" && input !== null && "deleted" in input && "future" in input) {
    return {
      is_deleted: input.deleted, // renamed
      is_future: input.future, // renamed
      show_client_side: true, // removed
      ...input,
    };
  }
  return input;
}

export const BridgeTransactionSchema = z.preprocess(
  migrateTransaction2025To2021,
  BridgeTransactionSchema2021,
);
export const BridgeTransactionLooseSchema = z.preprocess(
  migrateTransaction2025To2021,
  z.looseObject(BridgeTransactionSchema2021.shape),
);
export type BridgeTransaction = z.infer<typeof BridgeTransactionSchema> & {
  /** Aircap category override / seeded from mapped category_id. Not overwritten by Bridge sync. */
  userCategoryId?: string;
  trackedCategorySplits?: TrackedCategorySplit[];
  /** AI suggestion for a tracked category; present means pending user accept/reject. */
  suggestedTrackedCategoryId?: string;
  /** Last AI suggestion the user rejected for this transaction. */
  rejectedSuggestedTrackedCategoryId?: string;
  is_flagged?: boolean;
};

export const BridgeAccountDetailsSchema2021 = z.object({
  id: z.number(),
  name: z.string(),
  balance: z.number(),
  currency_code: z.string(),
  iban: z.string().nullish().default(null),
  status: z.int(),
});

// Migrate 2025 changes back to 2021 version
function migrateAccountDetails2025To2021(input: unknown) {
  if (typeof input === "object" && input !== null && "last_refresh_status" in input) {
    return {
      status: input.last_refresh_status === "successful" ? 0 : 1,
      ...input,
    };
  }
  return input;
}

export const BridgeAccountDetailsSchema = z.preprocess(
  migrateAccountDetails2025To2021,
  BridgeAccountDetailsSchema2021,
);
export const BridgeAccountDetailsLooseSchema = z.preprocess(
  migrateAccountDetails2025To2021,
  z.looseObject(BridgeAccountDetailsSchema2021.shape),
);
export type BridgeAccountDetails = z.infer<typeof BridgeAccountDetailsSchema>;
export type BridgeAccount = {
  details: BridgeAccountDetails;
  item_id: number;
  synced_at?: Timestamp;
  updated_at?: string;
  isDeleted?: boolean;
};
